import React, { useCallback, useEffect, useState } from "react";
import Header from "../header";
import SearchField from "../../searchField";
import Button from "../../Button";
import { FiPlus } from "react-icons/fi";
import Modal from "../../modal";
import DateInput from "../../dateInput";
import Input from "../../Input";
import SelectInput from "../../selectInput";
import TabManager from "../../tabManager";
import ActiveAssetsTable from "../tables/Assets-Table/activeAssetsTable";
import SoldAssetsTable from "../tables/Assets-Table/soldAssetsTable";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getPayment } from "../../../redux/payment/action";
import dayjs from "dayjs";
import { AddAssets, GetAssets } from "../../../redux/Assets/action";
import { validateAddAssetForm } from "../../../validation/ValidationAssets";

const Assets = () => {
  const tabItems = [
    {
      title: "Active Assets",
      key: "ActiveAssets",
    },
    {
      title: "Sold Assets",
      key: "SoldAssets",
    },
  ];

  const [filter] = useState({
    page: 1,
    limit: 10,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState({
    date: "",
    amount: "",
    title: "",
    paymentmethod: "",
    description: "",
  });

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("ActiveAssets");
  const [searchValue, setSearchValue] = useState(""); // State for search input
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  // Other states
  const [paymentPage, setPaymentPage] = useState(20);

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
    resetForm();
  };

  const getPaymentData = useSelector(
    (state) => state?.Payment?.getPayment?.data
  );

  const paymentOption = getPaymentData?.map((e) => {
    return {
      lable: e?.paymentmethod,
      value: e?._id,
    };
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (isSubmit) {
      const validationErrors = validateAddAssetForm({
        ...data,
        [name]: value,
      });
      setErrors(validationErrors);
    }
  };

  const handleDateChange = (date, dateString) => {
    setData((prevData) => ({
      ...prevData,
      date: dateString,
    }));

    if (isSubmit) {
      const validationErrors = validateAddAssetForm({
        ...data,
        date: dateString,
      });
      setErrors(validationErrors);
    }
  };

  const resetForm = () => {
    setData({
      date: null,
      amount: "",
      title: "",
      paymentmethod: "",
      description: "",
    });
  };

  const handleSubmit = async () => {
    setIsSubmit(true);
    const validationErrors = validateAddAssetForm(data);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      try {
        setLoading(true);
        let obj = {
          date: data?.date,
          amount: Number(data?.amount),
          title: data?.title,
          paymentmethod: data?.paymentmethod,
          description: data?.description,
        };

        await dispatch(AddAssets(obj)).then((res) => {
          if (res?.status === 200) {
            resetForm();
            dispatch(
              GetAssets({
                page: filter?.page,
                limit: filter?.limit,
                type: false,
                search: searchValue,
              })
            );
            setLoading(false);
            closeModal();
          } else if (res?.status === 401) {
            setLoading(false);
            navigate("/");
          }
        });

        setLoading(false);
      } catch (error) {
        console.log("error", error);
        setLoading(false);
        return error;
      }
    }
  };

  const fetchMoreOptions = useCallback(() => {
    dispatch(
      getPayment({ page: 1, limit: paymentPage, search: searchValue })
    ).then((res) => {
      if (res?.status === 200) {
        setPaymentPage((prev) => prev + 10);
      }
    });
  }, [dispatch, paymentPage, searchValue]);

  useEffect(() => {
    dispatch(
      getPayment({
        page: 1,
        limit: 10,
        search: "",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    // Reset searchValue when activeTab changes
    setSearchValue("");
  }, [activeTab]);

  return (
    <div className="w-full px-8 py-2 flex flex-col gap-[30px]">
      <Header title={"Assets"} />

      <div className="flex items-center justify-between max-xl:flex-col max-xl:gap-3 ">
        <div className="max-xl:w-full">
          <TabManager
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabItems={tabItems}
          />
        </div>
        <div className="flex items-center justify-end gap-5">
          <SearchField
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
          {activeTab === "ActiveAssets" && (
            <Button
              onClick={openModal}
              className={"bg-green hover:bg-darkgreen text-white"}
              icon={<FiPlus />}
              children={"Add New Asset"}
            />
          )}
        </div>
      </div>

      <Modal
        loading={loading}
        modalWidth={"w-1/2"}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        openModal={openModal}
        closeModal={closeModal}
        imgSrc="/images/addAssets.svg"
        title="Add New Asset"
        btn1="Cancel"
        btn2="Add Asset"
        type2={"submit"}
        btnClass={"justify-end"}
        modalContent={
          <div className="grid grid-cols-2 gap-x-5 gap-y-5 text-base font-normal">
            <div>
              <DateInput
                onChange={handleDateChange}
                value={data.date ? dayjs(data.date, "DD/MM/YYYY") : null}
                name={"date"}
              />
              {errors.date && <div className="text-red-500">{errors.date}</div>}
            </div>

            <div>
              <Input
                lableName={"Amount"}
                inputType={"number"}
                currency={true}
                onChange={handleChange}
                value={data?.amount}
                name={"amount"}
              />
              {errors.amount && (
                <div className="text-red-500">{errors.amount}</div>
              )}
            </div>

            <div>
              <Input
                lableName={"Title"}
                inputType={"text"}
                onChange={handleChange}
                value={data?.title}
                name={"title"}
              />
              {errors.title && (
                <div className="text-red-500">{errors.title}</div>
              )}
            </div>

            <div>
              <SelectInput
                lableName={"Payment Method"}
                option={paymentOption}
                onChange={handleChange}
                value={data?.paymentmethod}
                name={"paymentmethod"}
                fetchMoreOptions={fetchMoreOptions}
              />
              {errors.paymentmethod && (
                <div className="text-red-500">{errors.paymentmethod}</div>
              )}
            </div>

            <div>
              <Input
                lableName={"Description"}
                textarea={true}
                onChange={handleChange}
                value={data?.description}
                name={"description"}
              />
              {errors.description && (
                <div className="text-red-500">{errors.description}</div>
              )}
            </div>
          </div>
        }
        handleBtn2={handleSubmit}
      />

      {activeTab === "ActiveAssets" && (
        <ActiveAssetsTable searchValue={searchValue} />
      )}
      {activeTab === "SoldAssets" && (
        <SoldAssetsTable searchValue={searchValue} />
      )}
    </div>
  );
};

export default Assets;
